import { getFontFamilyCss } from '../../utils/fontHelper';
import React, { useState, useEffect, useMemo } from 'react';
import { Carousel } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getImageCandidateUrls } from '../../utils/imageHelper';

const BannerCarouselImage = ({ src, alt, className, style }) => {
  const candidateUrls = useMemo(() => (src ? getImageCandidateUrls(src) : []), [src]);
  const [candidateIdx, setCandidateIdx] = useState(0);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setCandidateIdx(0);
    setHasError(false);
  }, [src]);

  const handleError = () => {
    if (candidateIdx + 1 < candidateUrls.length) {
      setCandidateIdx(prev => prev + 1);
    } else {
      setHasError(true);
    }
  };

  const currentUrl = !hasError && candidateUrls.length > 0 ? candidateUrls[candidateIdx] : null;

  if (!src || !currentUrl) {
    return (
      <div
        className={className}
        style={{
          ...style,
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
        }}
      />
    );
  }

  return (
    <img
      src={currentUrl}
      alt={alt}
      className={className}
      style={style}
      onError={handleError}
    />
  );
};

export const CAROUSEL_SIZE_PRESETS = {
  slim: {
    id: 'slim',
    label: 'Panorámico Delgado (Slim)',
    description: 'Bajo y alargado (220px). Ocupa poco espacio vertical para priorizar los artículos.',
    aspectRatio: '1200 / 220',
    maxHeight: '220px',
    minHeight: '140px',
    recommendedResolution: '1200 x 220 px',
    badge: 'Compacto'
  },
  standard: {
    id: 'standard',
    label: 'Panorámico Estándar',
    description: 'Proporción equilibrada y estética. Recomendado para la mayoría de sitios y tiendas.',
    aspectRatio: '1200 / 320',
    maxHeight: '320px',
    minHeight: '180px',
    recommendedResolution: '1200 x 320 px',
    badge: 'Recomendado'
  },
  medium: {
    id: 'medium',
    label: 'Medio / Promocional (Medium)',
    description: 'Mayor altura (420px) para destacar fotos con textos grandes, promociones y combos.',
    aspectRatio: '1200 / 420',
    maxHeight: '420px',
    minHeight: '230px',
    recommendedResolution: '1200 x 420 px',
    badge: 'Promocional'
  },
  large: {
    id: 'large',
    label: 'Alto / Gran Impacto (Hero)',
    description: 'Gran presencia visual superior (520px) tipo portada institucional o editorial.',
    aspectRatio: '1200 / 520',
    maxHeight: '520px',
    minHeight: '280px',
    recommendedResolution: '1200 x 520 px',
    badge: 'Impacto Visual'
  },
  showcase: {
    id: 'showcase',
    label: 'Gigante / Portada Completa (Showcase)',
    description: 'Altura prominente (680px - 720px) para ambientaciones, salones, locales o productos de estilo deco/home.',
    aspectRatio: '16 / 9',
    maxHeight: '720px',
    minHeight: '380px',
    recommendedResolution: '1920 x 1080 px o 1600 x 900 px',
    badge: 'Showcase / Deco & Home'
  },
  fullscreen: {
    id: 'fullscreen',
    label: 'Hero Altura Completa (75vh)',
    description: 'Ocupa el 75% de la altura visible de la pantalla, ideal para tiendas modernas con fotografía de impacto.',
    aspectRatio: '16 / 9',
    maxHeight: '75vh',
    minHeight: '450px',
    recommendedResolution: '1920 x 1080 px',
    badge: 'Pantalla Completa'
  },
  ultrawide: {
    id: 'ultrawide',
    label: 'Cine Ultra-Ancho (21:9)',
    description: 'Formato ultra-panorámico horizontal de estilo cinematográfico moderno.',
    aspectRatio: '21 / 9',
    maxHeight: '360px',
    minHeight: '160px',
    recommendedResolution: '1680 x 720 px',
    badge: 'Ultra-Ancho'
  },
  classic: {
    id: 'classic',
    label: 'Formato Video (16:9)',
    description: 'Formato clásico estándar de video y redes sociales (460px).',
    aspectRatio: '16 / 9',
    maxHeight: '460px',
    minHeight: '220px',
    recommendedResolution: '1280 x 720 px',
    badge: '16:9'
  }
};

export const CAROUSEL_WIDTH_MODES = {
  container: {
    id: 'container',
    label: 'Encuadrado Estándar',
    description: 'Alineado con el ancho normal del contenedor de la página.',
    maxWidth: '1320px'
  },
  fullwidth: {
    id: 'fullwidth',
    label: 'Ancho Completo (Borde a Borde)',
    description: 'Ocupa el 100% de la pantalla sin márgenes laterales.',
    maxWidth: '100%'
  },
  compact: {
    id: 'compact',
    label: 'Compacto / Centrado',
    description: 'Ancho acotado con márgenes laterales amplios.',
    maxWidth: '1080px'
  }
};

export const DEFAULT_SECONDARY_BANNERS = [
  {
    id: 'sec-banner-1',
    src: '/images/banners/Caja.jpg',
    tag: 'LOGÍSTICA & DISTRIBUCIÓN',
    title: 'Hacemos envíos',
    titleFontWeight: 'regular',
    titleFontSize: 'medium',
    title2: '¡A TODO EL PAÍS!',
    title2FontWeight: 'black',
    title2FontSize: 'large',
    title2Highlight: false,
    subtitle: 'Llegamos a cada rincón con flota propia y expresos de confianza. Despachos rápidos y seguros para compras mayoristas y minoristas.',
    buttonText: 'Consultar Envíos',
    buttonLink: '/contacto',
    buttonStyle: 'primary',
    showButton1: true,
    secondaryButtonText: 'Ver Catálogo Online',
    secondaryButtonLink: '/tienda',
    button2Style: 'outline-dark',
    showButton2: true,
    layoutMode: 'split',
    textAlignment: 'left',
    textColorTheme: 'dark',
    backgroundColor: '#f8fafc',
    textShadow: 'none',
    is_active: true,
    order: 1
  }
];

export const DEFAULT_UNIFIED_BANNERS = [
  {
    id: 'banner-1',
    src: '/images/banners/Frente-Web1.jpg',
    tag: 'FABRICACIÓN DIRECTA',
    title: 'Envases, Embalajes y Productos de Cartón',
    subtitle: 'Más de 30 años acompañando a comercios, industrias y distribuidoras en todo el país.',
    buttonText: 'Explorar Catálogo',
    buttonLink: '/tienda',
    secondaryButtonText: 'Conocé Nuestra Empresa',
    secondaryButtonLink: '/empresa',
    is_active: true,
    order: 1
  },
  {
    id: 'banner-2',
    src: '/images/banners/Frente-Web2.jpg',
    tag: 'LÍNEA GASTRONÓMICA & DESCARTABLES',
    title: 'Soluciones Integrales para Gastronomía',
    subtitle: 'Bandejas, blondas, cajas de pizza, rollos de arranque, films y descartables.',
    buttonText: 'Ver Productos',
    buttonLink: '/tienda?rubro=Gastronomia',
    secondaryButtonText: 'Contáctenos',
    secondaryButtonLink: '/contacto',
    is_active: true,
    order: 2
  },
  {
    id: 'banner-3',
    src: '/images/banners/Caja.jpg',
    tag: 'PACKAGING & LOGÍSTICA',
    title: 'Cajas de Cartón y Cintas de Embalaje',
    subtitle: 'Formatos estándar y a medida con la máxima resistencia para tus envíos.',
    buttonText: 'Ver Cajas en Tienda',
    buttonLink: '/tienda',
    secondaryButtonText: 'Dónde Estamos',
    secondaryButtonLink: '/donde-estamos',
    is_active: true,
    order: 3
  },
  {
    id: 'banner-4',
    src: '/images/banners/Slide4.jpg',
    tag: 'VENTA MAYORISTA',
    title: 'Papelería Comercial y Librería',
    subtitle: 'Bobinas de papel, resmas, rollos térmicos y embalajes industriales.',
    buttonText: 'Consultar Precios',
    buttonLink: '/tienda?rubro=Papeleria',
    secondaryButtonText: '',
    secondaryButtonLink: '',
    is_active: true,
    order: 4
  }
];

const UnifiedHeroBanner = ({
  banners,
  carouselSettings = {},
  interval = 5000,
  showControls = true,
  showIndicators = true,
  className = ''
}) => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);

  // Filtrar activos y ordenar
  const activeBanners = (banners && Array.isArray(banners) && banners.length > 0)
    ? banners.filter(b => b.is_active !== false).sort((a, b) => (a.order || 0) - (b.order || 0))
    : DEFAULT_UNIFIED_BANNERS;

  if (activeBanners.length === 0) return null;

  const sizePresetKey = carouselSettings?.sizePreset || 'standard';
  const widthModeKey = carouselSettings?.widthMode || 'container';
  const fitMode = carouselSettings?.fitMode || 'cover';
  const borderRadius = carouselSettings?.borderRadius || '16px';

  // Configuración de auto-reproducción y tiempo de transición
  const effectiveInterval = (() => {
    if (carouselSettings?.autoPlayInterval !== undefined) {
      const num = Number(carouselSettings.autoPlayInterval);
      return num > 0 ? num : null;
    }
    if (carouselSettings?.autoPlay === false) return null;
    if (interval !== undefined && interval !== null) {
      const num = Number(interval);
      return num > 0 ? num : null;
    }
    return 5000;
  })();

  const effectiveShowControls = carouselSettings?.showControls !== undefined
    ? Boolean(carouselSettings.showControls)
    : showControls;

  const effectiveShowIndicators = carouselSettings?.showIndicators !== undefined
    ? Boolean(carouselSettings.showIndicators)
    : showIndicators;

  const selectedPreset = CAROUSEL_SIZE_PRESETS[sizePresetKey] || CAROUSEL_SIZE_PRESETS.standard;
  const selectedWidth = CAROUSEL_WIDTH_MODES[widthModeKey] || CAROUSEL_WIDTH_MODES.container;

  const wrapperStyle = {
    borderRadius: borderRadius,
    maxWidth: selectedWidth.maxWidth,
    margin: widthModeKey === 'compact' ? '0 auto 1.5rem auto' : '0 auto 1.5rem auto'
  };

  const itemHeightStyle = {
    aspectRatio: selectedPreset.aspectRatio,
    maxHeight: selectedPreset.maxHeight,
    minHeight: selectedPreset.minHeight,
    height: 'auto',
    borderRadius: borderRadius
  };

  // Botones de navegación laterales <> de alto contraste y diseño moderno
  const customPrevIcon = (
    <span 
      className="d-flex align-items-center justify-content-center bg-dark bg-opacity-75 text-white rounded-circle shadow-lg"
      style={{
        width: '44px',
        height: '44px',
        fontSize: '1.25rem',
        border: '2px solid rgba(255, 255, 255, 0.75)',
        backdropFilter: 'blur(4px)',
        boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
        transition: 'transform 0.2s ease, background-color 0.2s ease'
      }}
      title="Anterior"
    >
      <i className="bi bi-chevron-left" style={{ marginLeft: '-2px' }}></i>
    </span>
  );

  const customNextIcon = (
    <span 
      className="d-flex align-items-center justify-content-center bg-dark bg-opacity-75 text-white rounded-circle shadow-lg"
      style={{
        width: '44px',
        height: '44px',
        fontSize: '1.25rem',
        border: '2px solid rgba(255, 255, 255, 0.75)',
        backdropFilter: 'blur(4px)',
        boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
        transition: 'transform 0.2s ease, background-color 0.2s ease'
      }}
      title="Siguiente"
    >
      <i className="bi bi-chevron-right" style={{ marginRight: '-2px' }}></i>
    </span>
  );

  return (
    <div 
      className={`unified-hero-banner-container overflow-hidden shadow-sm border ${className}`}
      style={wrapperStyle}
    >
      {/* Estilos para asegurar alta visibilidad de los controles <> y los indicadores, más optimización responsive */}
      <style>{`
        .unified-hero-carousel .carousel-control-prev,
        .unified-hero-carousel .carousel-control-next {
          z-index: 5 !important;
          width: 7% !important;
          min-width: 56px !important;
          opacity: 0.88 !important;
          transition: all 0.2s ease-in-out !important;
        }
        .unified-hero-carousel .carousel-control-prev:hover,
        .unified-hero-carousel .carousel-control-next:hover {
          opacity: 1 !important;
          transform: scale(1.08);
        }
        .unified-hero-carousel .carousel-indicators {
          z-index: 5 !important;
          margin-bottom: 0.75rem;
        }
        .unified-hero-carousel .carousel-indicators [data-bs-target] {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          margin: 0 4px;
          background-color: #ffffff;
          opacity: 0.65;
          border: 1px solid rgba(0,0,0,0.3);
          transition: all 0.25s ease;
        }
        .unified-hero-carousel .carousel-indicators .active {
          width: 24px;
          border-radius: 12px;
          background-color: #ffc107;
          opacity: 1;
          border-color: #d39e00;
        }

        /* ===== OPTIMIZACIÓN RESPONSIVE AVANZADA (MOBILE < 768px): HERO INTEGRADO GLASSMORPHIC ===== */
        @media (max-width: 767.98px) {
          /* En modo Split en móvil: el slide mantiene una altura proporcional unificada (340px) en lugar de bloques separados */
          .unified-hero-slide-wrapper.is-split-mode {
            height: 340px !important;
            min-height: 340px !important;
            max-height: 360px !important;
            position: relative !important;
            overflow: hidden !important;
            display: block !important;
          }

          .hero-split-row {
            position: static !important;
            height: 100% !important;
            width: 100% !important;
            margin: 0 !important;
            display: block !important;
          }

          /* La imagen llena el fondo del slide con un enfoque visual en su zona superior */
          .hero-split-image-col {
            position: absolute !important;
            inset: 0 !important;
            width: 100% !important;
            height: 100% !important;
            z-index: 1 !important;
          }

          .hero-split-image-col img,
          .hero-split-image-col .hero-banner-image {
            width: 100% !important;
            height: 100% !important;
            object-fit: cover !important;
            object-position: center 20% !important;
          }

          /* Velo sutil con degradé para garantizar que la imagen y los textos convivan con máximo contraste */
          .hero-split-image-col::after {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 30%, rgba(15,23,42,0.45) 100%);
            pointer-events: none;
            z-index: 2;
          }

          .unified-hero-slide-wrapper.theme-dark-text .hero-split-image-col::after {
            background: linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(248,250,252,0.2) 25%, rgba(248,250,252,0.7) 100%);
          }

          /* Tarjeta de contenido flotante con Glassmorphism moderno (vidrio esmerilado + blur) */
          .hero-split-text-col {
            position: absolute !important;
            bottom: 20px !important;
            left: 12px !important;
            right: 12px !important;
            width: calc(100% - 24px) !important;
            max-width: none !important;
            height: auto !important;
            z-index: 3 !important;
            padding: 0.95rem 1rem !important;
            border-radius: 16px !important;
            background: rgba(255, 255, 255, 0.92) !important;
            backdrop-filter: blur(14px) !important;
            -webkit-backdrop-filter: blur(14px) !important;
            border: 1px solid rgba(255, 255, 255, 0.8) !important;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.16), 0 8px 10px -6px rgba(0, 0, 0, 0.08) !important;
          }

          .hero-split-text-col.is-theme-light {
            background: rgba(15, 23, 42, 0.88) !important;
            border: 1px solid rgba(255, 255, 255, 0.16) !important;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.45) !important;
          }

          /* En modo Cover con texto: misma estética flotante de tarjeta glassmorphic */
          .unified-hero-slide-wrapper.is-cover-mode.has-text-content {
            height: 340px !important;
            min-height: 340px !important;
            max-height: 360px !important;
          }

          .unified-hero-slide-wrapper.is-cover-mode.has-text-content .hero-cover-image {
            object-fit: cover !important;
            object-position: center 20% !important;
          }

          .unified-hero-slide-wrapper.is-cover-mode.has-text-content .hero-cover-overlay {
            background: none !important;
            padding: 0 !important;
            align-items: flex-end !important;
          }

          .unified-hero-slide-wrapper.is-cover-mode.has-text-content .hero-cover-overlay .container {
            padding: 0 12px 20px 12px !important;
            max-width: 100% !important;
          }

          .unified-hero-slide-wrapper.is-cover-mode.has-text-content .hero-cover-overlay [class*="col-"] {
            padding: 0.95rem 1rem !important;
            border-radius: 16px !important;
            background: rgba(255, 255, 255, 0.92) !important;
            backdrop-filter: blur(14px) !important;
            -webkit-backdrop-filter: blur(14px) !important;
            border: 1px solid rgba(255, 255, 255, 0.8) !important;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.16), 0 8px 10px -6px rgba(0, 0, 0, 0.08) !important;
          }

          .unified-hero-slide-wrapper.is-cover-mode.has-text-content .hero-cover-overlay.is-theme-light [class*="col-"] {
            background: rgba(15, 23, 42, 0.88) !important;
            border: 1px solid rgba(255, 255, 255, 0.16) !important;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.45) !important;
          }

          /* Ajustes de tipografía para proporciones armónicas en mobile */
          .hero-banner-title {
            font-size: clamp(1rem, 4.2vw, 1.25rem) !important;
            line-height: 1.2 !important;
            margin-bottom: 0.2rem !important;
          }
          .hero-banner-title2 {
            font-size: clamp(1.15rem, 5vw, 1.45rem) !important;
            line-height: 1.2 !important;
            margin-bottom: 0.55rem !important;
          }

          /* Flechas de navegación ubicadas en la zona superior de la foto sin tapar la tarjeta */
          .unified-hero-carousel .carousel-control-prev,
          .unified-hero-carousel .carousel-control-next {
            height: 130px !important;
            top: 10px !important;
            bottom: auto !important;
            width: 36px !important;
            min-width: 36px !important;
            z-index: 5 !important;
          }

          .unified-hero-carousel .carousel-control-prev span,
          .unified-hero-carousel .carousel-control-next span {
            width: 30px !important;
            height: 30px !important;
            font-size: 0.85rem !important;
            background-color: rgba(15, 23, 42, 0.75) !important;
            border: 1px solid rgba(255, 255, 255, 0.8) !important;
          }

          /* Indicadores (dots) ubicados prolijamente debajo de la tarjeta */
          .unified-hero-carousel .carousel-indicators {
            bottom: 4px !important;
            margin-bottom: 0 !important;
            z-index: 5 !important;
          }

          .unified-hero-carousel .carousel-indicators [data-bs-target] {
            width: 7px !important;
            height: 7px !important;
            margin: 0 3px !important;
          }

          .unified-hero-carousel .carousel-indicators .active {
            width: 18px !important;
          }
        }
      `}</style>

      <Carousel
        activeIndex={activeIndex}
        onSelect={(idx) => setActiveIndex(idx)}
        interval={effectiveInterval}
        pause="hover"
        fade={carouselSettings?.transitionEffect === 'fade' || carouselSettings?.fade === true}
        indicators={effectiveShowIndicators && activeBanners.length > 1}
        controls={effectiveShowControls && activeBanners.length > 1}
        prevIcon={customPrevIcon}
        nextIcon={customNextIcon}
        className="unified-hero-carousel"
      >
        {activeBanners.map((slide, idx) => {
          const hasTag = Boolean(slide.tag && slide.tag.trim());
          const hasTitle = Boolean(slide.title && slide.title.trim());
          const hasSubtitle = Boolean(slide.subtitle && slide.subtitle.trim());
          const hasPrimaryBtn = slide.showButton1 !== false && Boolean(slide.buttonText && slide.buttonText.trim());
          const hasSecondaryBtn = slide.showButton2 !== false && Boolean(slide.secondaryButtonText && slide.secondaryButtonText.trim());
          const hasTextContent = hasTag || hasTitle || hasSubtitle || hasPrimaryBtn || hasSecondaryBtn;

          // Si el banner está limpio (sin texto) y tiene un link configurado, hacer clicable toda la imagen
          const fallbackLink = slide.buttonLink || slide.link || '';
          const isImageClickable = !hasTextContent && Boolean(fallbackLink);

          const handleSlideClick = () => {
            if (isImageClickable && fallbackLink) {
              if (fallbackLink.startsWith('http')) {
                window.open(fallbackLink, '_blank');
              } else {
                navigate(fallbackLink);
              }
            }
          };

          // Configuración avanzada de layout y estilo por banner
          const layoutMode = slide.layoutMode || 'cover'; // 'cover' | 'split' | 'split-reverse'
          const textAlignment = slide.textAlignment || 'left'; // 'left' | 'center' | 'right'
          const imagePositionMap = {
            center: 'center center',
            right: 'right center',
            left: 'left center',
            top: 'center top',
            bottom: 'center bottom'
          };
          const objectPosition = imagePositionMap[slide.imagePosition] || 'center center';
          const isDarkText = slide.textColorTheme === 'dark';
          const titleColor = isDarkText ? '#0f172a' : '#ffffff';
          const subtitleColor = isDarkText ? '#334155' : 'rgba(255, 255, 255, 0.9)';
          const showOverlay = slide.showOverlay !== false && carouselSettings?.showOverlay !== false;

          // Tipografía personalizada
          const fontCss = getFontFamilyCss(slide.fontFamily || carouselSettings?.fontFamily);
          const fontStyle = fontCss ? { fontFamily: fontCss } : {};

          // Gradiente dinámico según alineación y tema de texto
          let overlayBackground = 'transparent';
          if (showOverlay && layoutMode === 'cover') {
            if (isDarkText) {
              if (textAlignment === 'right') {
                overlayBackground = 'linear-gradient(to left, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0.6) 50%, rgba(255, 255, 255, 0) 100%)';
              } else if (textAlignment === 'center') {
                overlayBackground = 'radial-gradient(circle, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.4) 60%, transparent 100%)';
              } else {
                overlayBackground = 'linear-gradient(to right, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0.6) 50%, rgba(255, 255, 255, 0) 100%)';
              }
            } else {
              if (textAlignment === 'right') {
                overlayBackground = 'linear-gradient(to left, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.55) 50%, rgba(0, 0, 0, 0.15) 100%)';
              } else if (textAlignment === 'center') {
                overlayBackground = 'radial-gradient(circle, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.45) 60%, transparent 100%)';
              } else {
                overlayBackground = 'linear-gradient(to right, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.55) 50%, rgba(0, 0, 0, 0.15) 100%)';
              }
            }
          }

          // Estilo de botones personalizables
          const resolveButtonClass = (styleName, defaultStyle) => {
            const chosen = styleName || defaultStyle;
            switch (chosen) {
              case 'primary':
                return 'btn btn-primary shadow-sm text-white';
              case 'accent':
                return 'btn btn-warning text-dark fw-bold shadow-sm';
              case 'dark':
                return 'btn btn-dark text-white shadow-sm';
              case 'white':
                return 'btn btn-light text-dark border shadow-sm';
              case 'outline-dark':
                return 'btn btn-outline-dark fw-semibold';
              case 'outline-light':
                return 'btn btn-outline-light fw-semibold';
              case 'outline-primary':
                return 'btn btn-outline-primary fw-semibold';
              case 'secondary':
                return 'btn btn-secondary text-white shadow-sm';
              case 'outline':
                return isDarkText ? 'btn btn-outline-dark fw-semibold' : 'btn btn-outline-light fw-semibold';
              default:
                return 'btn btn-primary shadow-sm';
            }
          };

          const primaryBtnClass = resolveButtonClass(slide.button1Style || slide.buttonStyle, 'primary');
          const secondaryBtnClass = resolveButtonClass(slide.button2Style, isDarkText ? 'outline-dark' : 'outline-light');

          // Sombra de textos: default a 'none' (elimina el relieve raro de 3D)
          let textShadowStyle = 'none';
          if (slide.textShadow === 'subtle') {
            textShadowStyle = '0 1px 4px rgba(0, 0, 0, 0.45)';
          } else if (slide.textShadow === 'strong') {
            textShadowStyle = '0 2px 8px rgba(0, 0, 0, 0.75)';
          } else if (slide.textShadow === 'none') {
            textShadowStyle = 'none';
          } else {
            textShadowStyle = 'none';
          }

          // Componente interno con los textos y llamados a la acción
          const textBlockContent = (
            <div className={`w-100 ${textAlignment === 'center' ? 'text-center' : textAlignment === 'right' ? 'text-end ms-auto' : 'text-start'}`} style={fontStyle}>
              {/* Etiqueta / Tag */}
              {hasTag && (
                <span 
                  className="badge bg-warning text-dark fw-bold px-2.5 py-1 rounded-pill mb-2 text-uppercase d-inline-block shadow-sm"
                  style={{ fontSize: '0.72rem', letterSpacing: '0.8px' }}
                >
                  {slide.tag}
                </span>
              )}

              {/* Título Principal (Línea 1) */}
              {hasTitle && (
                <h2 
                  className="hero-banner-title tracking-tight lh-tight"
                  style={{
                    fontSize: slide.titleFontSize === 'small'
                      ? 'clamp(1.1rem, 2vw, 1.65rem)'
                      : slide.titleFontSize === 'large'
                      ? 'clamp(1.5rem, 3.5vw, 3rem)'
                      : slide.titleFontSize === 'xlarge'
                      ? 'clamp(1.8rem, 4.2vw, 3.6rem)'
                      : 'clamp(1.25rem, 2.8vw, 2.3rem)',
                    fontWeight: slide.titleFontWeight === 'regular'
                      ? 400
                      : slide.titleFontWeight === 'bold'
                      ? 700
                      : 800,
                    color: titleColor,
                    textShadow: textShadowStyle,
                    marginBottom: Boolean(slide.title2 && slide.title2.trim()) ? '0.2rem' : '0.5rem',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {slide.title}
                </h2>
              )}

              {/* Segundo Título (Línea 2 - uno debajo del otro con control de tamaño y negrita) */}
              {Boolean(slide.title2 && slide.title2.trim()) && (
                <h2 
                  className="hero-banner-title2 mb-2 tracking-tight lh-tight"
                  style={{
                    fontSize: slide.title2FontSize === 'small'
                      ? 'clamp(1.1rem, 2vw, 1.65rem)'
                      : slide.title2FontSize === 'large'
                      ? 'clamp(1.5rem, 3.5vw, 3rem)'
                      : slide.title2FontSize === 'xlarge'
                      ? 'clamp(1.8rem, 4.2vw, 3.6rem)'
                      : 'clamp(1.25rem, 2.8vw, 2.3rem)',
                    fontWeight: slide.title2FontWeight === 'regular'
                      ? 400
                      : slide.title2FontWeight === 'bold'
                      ? 700
                      : 800,
                    color: slide.title2Color || (slide.title2Highlight ? '#ffc107' : titleColor),
                    textShadow: textShadowStyle,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {slide.title2}
                </h2>
              )}

              {/* Subtítulo / Descripción */}
              {hasSubtitle && (
                <p 
                  className="mb-3 d-none d-sm-block lh-base"
                  style={{
                    fontSize: slide.subtitleFontSize === 'small'
                      ? 'clamp(0.75rem, 1vw, 0.9rem)'
                      : slide.subtitleFontSize === 'large'
                      ? 'clamp(1rem, 1.5vw, 1.25rem)'
                      : 'clamp(0.85rem, 1.2vw, 1.05rem)',
                    color: subtitleColor,
                    textShadow: textShadowStyle,
                    maxWidth: textAlignment === 'center' ? '700px' : '620px',
                    marginLeft: textAlignment === 'center' ? 'auto' : undefined,
                    marginRight: textAlignment === 'center' ? 'auto' : undefined
                  }}
                >
                  {slide.subtitle}
                </p>
              )}

              {/* Botones de Acción */}
              {(hasPrimaryBtn || hasSecondaryBtn) && (
                <div className={`d-flex flex-wrap align-items-center gap-2 mt-2 ${textAlignment === 'center' ? 'justify-content-center' : textAlignment === 'right' ? 'justify-content-end' : 'justify-content-start'}`}>
                  {hasPrimaryBtn && (
                    <button
                      type="button"
                      className={`btn-sm rounded-pill px-3 py-1.5 fw-bold d-inline-flex align-items-center gap-1.5 ${primaryBtnClass}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(slide.buttonLink || '/tienda');
                      }}
                    >
                      <span>{slide.buttonText}</span>
                      <i className="bi bi-arrow-right-short fs-5 lh-1"></i>
                    </button>
                  )}

                  {hasSecondaryBtn && (
                    <button
                      type="button"
                      className={`btn btn-sm rounded-pill px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1.5 ${secondaryBtnClass}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(slide.secondaryButtonLink || '/empresa');
                      }}
                    >
                      <span>{slide.secondaryButtonText}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          );

          return (
            <Carousel.Item key={slide.id || idx}>
              <div 
                className={`unified-hero-slide-wrapper position-relative overflow-hidden w-100 d-flex align-items-center ${layoutMode.startsWith('split') ? 'is-split-mode' : 'is-cover-mode'} ${hasTextContent ? 'has-text-content' : 'no-text-content'} ${isDarkText ? 'theme-dark-text' : 'theme-light-text'}`}
                style={{
                  ...itemHeightStyle,
                  cursor: isImageClickable ? 'pointer' : 'default',
                  backgroundColor: carouselSettings?.backgroundColor || '#f8fafc',
                  borderRadius: borderRadius
                }}
                onClick={handleSlideClick}
              >
                {/* MODO A: DIVIDIDO / SPLIT (Texto de un lado | Foto al costado) */}
                {layoutMode.startsWith('split') ? (
                  <div className={`hero-split-row row g-0 w-100 h-100 align-items-center ${layoutMode === 'split-reverse' ? 'flex-row-reverse' : ''}`}>
                    {/* Columna de Texto */}
                    {hasTextContent ? (
                      <div className={`hero-split-text-col col-12 col-md-6 p-4 p-md-5 d-flex align-items-center h-100 ${isDarkText ? 'is-theme-dark' : 'is-theme-light'}`}>
                        {textBlockContent}
                      </div>
                    ) : (
                      <div className="hero-split-text-col col-12 col-md-6 p-4 p-md-5 d-none d-md-flex align-items-center h-100">
                        <div className="text-muted small">Haz clic para ver más</div>
                      </div>
                    )}
                    {/* Columna de Imagen */}
                    <div className="hero-split-image-col col-12 col-md-6 h-100 position-relative overflow-hidden">
                      <BannerCarouselImage
                        src={slide.src}
                        alt={slide.title || slide.alt || `Banner #${idx + 1}`}
                        className="hero-banner-image w-100 h-100"
                        style={{
                          objectFit: fitMode,
                          objectPosition: objectPosition
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  /* MODO B: CLÁSICO / COVER (Foto de fondo con texto superpuesto) */
                  <>
                    <BannerCarouselImage
                      src={slide.src}
                      alt={slide.title || slide.alt || `Banner #${idx + 1}`}
                      className="hero-cover-image position-absolute top-0 start-0 w-100 h-100"
                      style={{
                        objectFit: fitMode,
                        objectPosition: objectPosition,
                        borderRadius: borderRadius
                      }}
                    />

                    {hasTextContent && (
                      <div 
                        className={`hero-cover-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center ${isDarkText ? 'is-theme-dark' : 'is-theme-light'} ${
                          textAlignment === 'center' ? 'justify-content-center' : textAlignment === 'right' ? 'justify-content-end' : 'justify-content-start'
                        }`}
                        style={{
                          background: overlayBackground,
                          borderRadius: borderRadius,
                          zIndex: 2
                        }}
                      >
                        <div className="container px-4 px-md-5 py-3">
                          <div className={textAlignment === 'center' ? 'col-12 col-md-10 col-lg-8 mx-auto' : 'col-12 col-md-9 col-lg-7'}>
                            {textBlockContent}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

              </div>
            </Carousel.Item>
          );
        })}
      </Carousel>
    </div>
  );
};

export default UnifiedHeroBanner;
